// Event times in the viewer's own time zone, with the zone spelled out.
//
// Events are stored as absolute instants. Until Sep 2026 every page rendered
// them in Pacific with a bare "PT", which made a Texas cohort and a UK sample
// do arithmetic on every listing. Now the browser's zone wins, Pacific is only
// the fallback when the browser can't say, and the abbreviation always rides
// along so nobody has to guess. US zones collapse to the two-letter forms Joe
// uses in email (PT/CT/MT/ET); everything else keeps what the browser reports.

export const viewerTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
};

const US_SHORT: Record<string, string> = {
  PST: 'PT', PDT: 'PT', CST: 'CT', CDT: 'CT', MST: 'MT', MDT: 'MT', EST: 'ET', EDT: 'ET',
  AKST: 'AKT', AKDT: 'AKT', HST: 'HT', HDT: 'HT',
};

// "PT", "CT", "BST", "GMT+2" for the given instant in the viewer's zone.
export const zoneAbbrev = (d: Date, timeZone = viewerTimeZone()): string => {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(d).find(p => p.type === 'timeZoneName')?.value || '';
    return US_SHORT[part] || part;
  } catch {
    return '';
  }
};

export interface EventDateParts {
  month: string;   // "SEP"
  day: string;     // "15"
  full: string;    // weekday/month/day (long or short per `style`)
  time: string;    // "5:00 PM PT"
  zone: string;    // "PT"
}

// The shape the event page, store page and tiles already consume.
export function formatEventParts(iso?: string | null, style: 'long' | 'short' = 'long'): EventDateParts | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const timeZone = viewerTimeZone();
  const zone = zoneAbbrev(d, timeZone);
  return {
    month: d.toLocaleDateString('en-US', { month: 'short', timeZone }).toUpperCase(),
    day: d.toLocaleDateString('en-US', { day: 'numeric', timeZone }),
    full: style === 'long'
      ? d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone })
      : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone }),
    time: `${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone })} ${zone}`.trim(),
    zone,
  };
}

// "Tuesday, September 15 at 5:00 PM PT"
export function formatEventLong(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const timeZone = viewerTimeZone();
  return `${d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone })} at ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone })} ${zoneAbbrev(d, timeZone)}`.trim();
}

// "Tuesday, September 15" (date only, viewer's zone)
export function formatEventDay(iso?: string | null, style: 'long' | 'short' = 'long'): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const timeZone = viewerTimeZone();
  return style === 'long'
    ? d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone })
    : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone });
}

// Human name of the viewer's zone for the builder's hint: "Central time (CT)".
export function viewerZoneLabel(): string {
  const tz = viewerTimeZone();
  const abbr = zoneAbbrev(new Date(), tz);
  const names: Record<string, string> = { PT: 'Pacific', CT: 'Central', MT: 'Mountain', ET: 'Eastern', AKT: 'Alaska', HT: 'Hawaii' };
  return names[abbr] ? `${names[abbr]} time (${abbr})` : `${tz.replace(/_/g, ' ')} (${abbr})`;
}
